import type { useResizable } from '../useResizable'
import type { AdminSport, Integration, IntegrationLeague, IntegrationStatus } from './api'
import { useEffect, useMemo, useState } from 'react'
import { categoryLabel, GROUP_ORDER } from '../api'
import { createIntegration, deleteIntegration, fetchIntegrationsCsv, integrationKeyFromLabel, renameIntegration } from './api'
import IntegrationLeagueEditor from './IntegrationLeagueEditor'
import { countByStatus, MarketChip, ReviewNotes, STATUS_META, STATUS_ORDER, StatusLegend } from './IntegrationStatus'

// /distribution INTEGRATIONS tab — one feed integration (Openbet, Abelson,
// InnProjekt / InnHub, Hard Rock) at a time: the leagues it carries, grouped
// by catalog category, with a status chip per market. Editable (migration
// 0070) for the write tier: per-league market editor, add/remove leagues,
// create / rename / delete integrations, CSV export of the live state.

interface Props {
  integrations: Integration[] | null
  sports: AdminSport[] | null
  selectedKey: string | null
  canEdit: boolean
  onSelect: (key: string) => void
  onOpenSport: (slug: string) => void
  // Fired after any write so AdminApp refetches the list.
  onChanged: () => void
  sidebar: ReturnType<typeof useResizable>
}

type Filter = 'all' | IntegrationStatus | 'review'

const NOT_IN_CATALOG = 'NOT IN CATALOG'

function groupLeagues(leagues: IntegrationLeague[]): Map<string, IntegrationLeague[]> {
  const groups = new Map<string, IntegrationLeague[]>()
  for (const cat of GROUP_ORDER) groups.set(cat, [])
  for (const l of leagues) {
    const label = l.sport_id == null ? NOT_IN_CATALOG : categoryLabel(l.sport_category)
    if (!groups.has(label)) groups.set(label, [])
    groups.get(label)!.push(l)
  }
  // Uncatalogued leagues always sit last so the real sports read first.
  const uncatalogued = groups.get(NOT_IN_CATALOG)
  groups.delete(NOT_IN_CATALOG)
  if (uncatalogued && uncatalogued.length > 0) groups.set(NOT_IN_CATALOG, uncatalogued)
  for (const [k, v] of groups) {
    if (v.length === 0) groups.delete(k)
    else v.sort((a, b) => a.league_name.localeCompare(b.league_name))
  }
  return groups
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export default function IntegrationsPane({ integrations, sports, selectedKey, canEdit, onSelect, onOpenSport, onChanged, sidebar }: Props) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  // One league block in edit mode at a time; 'new' = the ADD LEAGUE form.
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  const selected = integrations?.find(i => i.key === selectedKey) ?? null

  // Leave edit mode whenever the selection changes.
  useEffect(() => {
    setEditing(null)
    setRenaming(false)
    setMsg(null)
  }, [selectedKey])

  const filteredList = useMemo(() => {
    if (!integrations) return null
    const q = search.trim().toLowerCase()
    return q
      ? integrations.filter(i => i.name.toLowerCase().includes(q) || i.key.toLowerCase().includes(q))
      : integrations
  }, [integrations, search])

  const allMarkets = useMemo(() => selected?.leagues.flatMap(l => l.markets) ?? [], [selected])
  const counts = useMemo(() => countByStatus(allMarkets), [allMarkets])
  const reviewCount = allMarkets.filter(m => m.needs_review).length

  // Every league any integration lists — the editor reuses an existing key
  // for a sport instead of minting a second one.
  const knownLeagues = useMemo(() => {
    const seen = new Map<string, Pick<IntegrationLeague, 'league_key' | 'league_name' | 'sport_id'>>()
    for (const i of integrations ?? []) {
      for (const l of i.leagues) {
        if (!seen.has(l.league_key))
          seen.set(l.league_key, { league_key: l.league_key, league_name: l.league_name, sport_id: l.sport_id })
      }
    }
    return [...seen.values()]
  }, [integrations])

  // Apply the status filter per market, then drop leagues left with nothing
  // to show. 'review' keeps only flagged markets regardless of status.
  const visibleLeagues = useMemo(() => {
    if (!selected) return []
    if (filter === 'all') return selected.leagues
    return selected.leagues
      .map(l => ({
        ...l,
        markets: l.markets.filter(m => filter === 'review' ? m.needs_review : m.status === filter),
      }))
      .filter(l => l.markets.length > 0)
  }, [selected, filter])

  const grouped = useMemo(() => groupLeagues(visibleLeagues), [visibleLeagues])

  const filters: { id: Filter, label: string, count: number }[] = [
    { id: 'all', label: 'ALL', count: allMarkets.length },
    ...STATUS_ORDER.map(s => ({ id: s as Filter, label: STATUS_META[s].label, count: counts[s] })),
    { id: 'review', label: '⚠ REVIEW', count: reviewCount },
  ]

  function saved(text: string) {
    setEditing(null)
    setMsg({ kind: 'ok', text })
    onChanged()
  }

  async function submitRename() {
    if (!selected) return
    const name = nameDraft.trim()
    if (!name || name === selected.name) {
      setRenaming(false)
      return
    }
    setBusy(true)
    setMsg(null)
    try {
      await renameIntegration(selected.id, name)
      setRenaming(false)
      setMsg({ kind: 'ok', text: `Renamed to ${name}.` })
      onChanged()
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  async function removeIntegration() {
    if (!selected) return
    const rows = allMarkets.length
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${selected.name}? ${selected.leagues.length} league${selected.leagues.length === 1 ? '' : 's'} and ${rows} market row${rows === 1 ? '' : 's'} go with it. The audit log keeps the record.`))
      return
    setBusy(true)
    setMsg(null)
    try {
      await deleteIntegration(selected.id)
      onChanged()
      const next = integrations?.find(i => i.key !== selected.key)
      if (next) onSelect(next.key)
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
      setBusy(false)
    }
  }

  async function exportCsv() {
    setBusy(true)
    setMsg(null)
    try {
      downloadBlob(await fetchIntegrationsCsv(), 'integration_markets.csv')
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  const takenKeys = selected?.leagues.map(l => l.league_key) ?? []

  function renderLeague(l: IntegrationLeague) {
    if (selected && editing === l.league_key) {
      return (
        <IntegrationLeagueEditor
          key={`edit-${l.league_key}`}
          integrationId={selected.id}
          integrationName={selected.name}
          initial={l}
          sports={sports}
          takenKeys={takenKeys}
          knownLeagues={knownLeagues}
          onSaved={() => saved(`Saved ${l.league_name}.`)}
          onCancel={() => setEditing(null)}
        />
      )
    }
    return (
      <div key={l.league_key} className="ad-int-league">
        <div className="ad-int-league-head">
          <span className="ad-int-league-name">
            {l.league_name}
            <span className="b-mono-dim ad-list-slug">
              {' '}
              ·
              {l.league_key}
            </span>
          </span>
          <span className="ad-int-league-tools">
            {l.sport_slug
              ? (
                  <a
                    href={`/distribution/sports/${l.sport_slug}`}
                    className="ad-int-league-link"
                    onClick={(e) => {
                      e.preventDefault()
                      onOpenSport(l.sport_slug!)
                    }}
                  >
                    {l.sport_name}
                    {' '}
                    →
                  </a>
                )
              : <span className="b-mono-dim b-tiny">{NOT_IN_CATALOG}</span>}
            {canEdit && (
              <button
                type="button"
                className="b-btn b-btn-tiny"
                onClick={() => { setEditing(l.league_key); setMsg(null) }}
                disabled={busy || editing != null}
              >
                EDIT
              </button>
            )}
          </span>
        </div>
        <div className="ad-int-chips">
          {l.markets.map(m => <MarketChip key={m.market_key} market={m} />)}
        </div>
        <ReviewNotes markets={l.markets} />
      </div>
    )
  }

  return (
    <div className="ad-pane" style={{ '--sidebar-width': `${sidebar.width}px` } as React.CSSProperties}>
      <aside className="ad-list-pane bm-hide-mobile">
        <div className="ad-list-search">
          <span className="b-mono-dim">/</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="filter integrations…"
            className="ad-list-search-input"
            spellCheck={false}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="ad-list-search-clear"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="b-btn b-btn-primary ad-list-new-btn"
            >
              + NEW
            </button>
          )}
        </div>
        {!filteredList
          ? <p className="b-mono-dim">LOADING…</p>
          : filteredList.length === 0
            ? <p className="b-mono-dim ad-list-empty-search">no matches</p>
            : (
                <div className="ad-list-group is-open">
                  <div className="ad-list-group-head ad-list-group-head--static" aria-hidden="true">
                    <span className="ad-list-group-label">INTEGRATIONS</span>
                    <span className="ad-list-group-count">{filteredList.length}</span>
                  </div>
                  {filteredList.map((i) => {
                    const active = i.key === selectedKey
                    const live = i.leagues.filter(l => l.markets.some(m => m.status === 'live')).length
                    return (
                      <button
                        type="button"
                        key={i.key}
                        onClick={() => onSelect(i.key)}
                        className={`ad-list-row${active ? ' is-active' : ''}`}
                      >
                        <span className="ad-list-name">
                          {i.name}
                          <span className="b-mono-dim ad-list-slug">
                            {' '}
                            ·
                            {i.key}
                          </span>
                        </span>
                        <span className="b-mono-dim b-tiny">
                          {live}
                          /
                          {i.leagues.length}
                          {' '}
                          LIVE
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
        <button
          type="button"
          className={`ad-resize-handle${sidebar.isDragging ? ' is-dragging' : ''}`}
          onMouseDown={sidebar.onMouseDown}
          aria-label="Resize sidebar"
        />
      </aside>

      <div className="ad-edit-pane">
        {!integrations
          ? <p className="b-mono-dim">LOADING…</p>
          : !selected
              ? (
                  <div className="ad-msg">
                    {integrations.length === 0 ? 'No integrations yet — use + NEW.' : 'Select an integration from the list'}
                  </div>
                )
              : (
                  <section className={`ad-editor${editing == null ? ' is-read-only' : ''}`}>
                    <header className="ad-editor-head">
                      <div className="ad-editor-head-stack">
                        {renaming
                          ? (
                              <input
                                type="text"
                                value={nameDraft}
                                onChange={e => setNameDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') void submitRename()
                                  if (e.key === 'Escape') setRenaming(false)
                                }}
                                className="ad-input"
                                autoFocus
                                disabled={busy}
                              />
                            )
                          : <h2 className="b-h2">{selected.name}</h2>}
                        <span className="b-mono-dim b-tiny">
                          {selected.key}
                          {' · '}
                          {selected.leagues.length}
                          {' '}
                          LEAGUES
                          {' · '}
                          {allMarkets.length}
                          {' '}
                          MARKET ROWS
                        </span>
                      </div>
                      <div className="ad-editor-actions">
                        <button type="button" className="b-btn-ghost" onClick={exportCsv} disabled={busy}>
                          EXPORT CSV
                        </button>
                        {canEdit && !renaming && (
                          <>
                            <button
                              type="button"
                              className="b-btn-ghost"
                              onClick={() => { setNameDraft(selected.name); setRenaming(true); setMsg(null) }}
                              disabled={busy}
                            >
                              RENAME
                            </button>
                            <button type="button" className="b-btn-ghost" onClick={removeIntegration} disabled={busy}>
                              DELETE
                            </button>
                          </>
                        )}
                        {canEdit && renaming && (
                          <>
                            <button type="button" className="b-btn-ghost" onClick={() => setRenaming(false)} disabled={busy}>
                              CANCEL
                            </button>
                            <button type="button" className="b-btn b-btn-primary" onClick={submitRename} disabled={busy || !nameDraft.trim()}>
                              {busy ? 'SAVING…' : 'SAVE →'}
                            </button>
                          </>
                        )}
                      </div>
                    </header>

                    {msg && (
                      <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>
                        {msg.kind === 'err' ? '⚠' : '✓'}
                        {' '}
                        {msg.text}
                      </p>
                    )}

                    <p className="b-mono-dim ad-section-hint">
                      Leagues this integration carries and where each market stands.
                      {canEdit
                        ? ' Edits save straight to the database and land in the AUDIT LOG; EXPORT CSV hands the live state back to the trading sheet.'
                        : ' Read-only for your role.'}
                    </p>

                    <div className="ad-int-summary">
                      {STATUS_ORDER.map(s => (
                        <span key={s} className={`ad-int-stat ${STATUS_META[s].cls}`}>
                          <b>{counts[s]}</b>
                          {STATUS_META[s].label}
                        </span>
                      ))}
                      <span className="ad-int-stat is-review">
                        <b>{reviewCount}</b>
                        NEEDS REVIEW
                      </span>
                    </div>

                    <div className="ad-int-filter" role="tablist" aria-label="Filter markets by status">
                      {filters.map(f => (
                        <button
                          type="button"
                          key={f.id}
                          role="tab"
                          aria-selected={filter === f.id}
                          className={filter === f.id ? 'is-active' : ''}
                          onClick={() => setFilter(f.id)}
                        >
                          {f.label}
                          {' '}
                          <span className="ad-int-filter-count">{f.count}</span>
                        </button>
                      ))}
                    </div>

                    <StatusLegend />

                    {grouped.size === 0 && editing !== 'new'
                      ? (
                          <p className="b-mono-dim ad-list-empty">
                            {selected.leagues.length === 0 ? 'no leagues yet' : 'nothing matches this filter'}
                          </p>
                        )
                      : [...grouped.entries()].map(([cat, leagues]) => (
                          <div key={cat} className="ad-editor-section">
                            <h3 className="b-mono">
                              {cat}
                              {' '}
                              <span className="b-mono-dim">
                                ·
                                {' '}
                                {leagues.length}
                              </span>
                            </h3>
                            {leagues.map(renderLeague)}
                          </div>
                        ))}

                    {canEdit && editing === 'new' && (
                      <div className="ad-editor-section">
                        <h3 className="b-mono">NEW LEAGUE</h3>
                        <IntegrationLeagueEditor
                          key="new"
                          integrationId={selected.id}
                          integrationName={selected.name}
                          initial={{ league_key: null, league_name: '', sport_id: null, markets: [] }}
                          sports={sports}
                          takenKeys={takenKeys}
                          knownLeagues={knownLeagues}
                          onSaved={() => saved('League added.')}
                          onCancel={() => setEditing(null)}
                        />
                      </div>
                    )}

                    {canEdit && editing == null && (
                      <div className="ad-section-actions">
                        <button type="button" className="b-btn b-btn-primary" onClick={() => { setEditing('new'); setMsg(null) }} disabled={busy}>
                          + ADD LEAGUE
                        </button>
                      </div>
                    )}
                  </section>
                )}
      </div>

      {showNew && (
        <NewIntegrationDialog
          existingKeys={(integrations ?? []).map(i => i.key)}
          onClose={() => setShowNew(false)}
          onCreated={(key) => {
            setShowNew(false)
            onChanged()
            onSelect(key)
          }}
        />
      )}
    </div>
  )
}

// "+ NEW" — name + derived key (editable). Leagues are added afterwards
// from the detail pane, the same way a sportsbook gets its footprint after
// NewSportsbookDialog creates the row.
function NewIntegrationDialog({ existingKeys, onClose, onCreated }: {
  existingKeys: string[]
  onClose: () => void
  onCreated: (key: string) => void
}) {
  const [name, setName] = useState('')
  const [keyDraft, setKeyDraft] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const key = keyDraft ?? integrationKeyFromLabel(name)
  const keyValid = /^[a-z0-9_]+$/.test(key)
  const keyTaken = existingKeys.includes(key)
  const canSubmit = name.trim() !== '' && keyValid && !keyTaken && !busy

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const row = await createIntegration(key, name.trim())
      onCreated(row.key)
    }
    catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <div
      className="ad-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      <div className="ad-modal" role="dialog" aria-modal="true" aria-label="Create new integration">
        <header className="ad-modal-head">
          <h2 className="b-h2">NEW INTEGRATION</h2>
          <button type="button" className="b-btn-ghost" onClick={onClose} aria-label="Close" disabled={busy}>✕</button>
        </header>
        <label className="ad-field">
          <span className="b-mono-dim b-tiny">NAME</span>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="ad-input"
            placeholder="e.g. Kambi"
            autoFocus
            disabled={busy}
          />
        </label>
        <label className="ad-field">
          <span className="b-mono-dim b-tiny">KEY — lowercase snake_case, used in the URL, cannot change later</span>
          <input
            type="text"
            value={key}
            onChange={e => setKeyDraft(e.target.value)}
            className="ad-input"
            spellCheck={false}
            disabled={busy}
          />
        </label>
        {key !== '' && !keyValid && <p className="ad-msg b-mono is-err">key must be lowercase letters, digits and underscores</p>}
        {keyTaken && (
          <p className="ad-msg b-mono is-err">
            {key}
            {' '}
            already exists
          </p>
        )}
        {error && <p className="ad-msg b-mono is-err">{error}</p>}
        <div className="ad-modal-actions">
          <button type="button" className="b-btn-ghost" onClick={onClose} disabled={busy}>CANCEL</button>
          <button type="button" className="b-btn b-btn-primary" onClick={submit} disabled={!canSubmit}>
            {busy ? 'CREATING…' : 'CREATE →'}
          </button>
        </div>
      </div>
    </div>
  )
}
